import { Form, Card, Typography, Input, FormInstance } from 'antd';
import { useEffect, useMemo } from 'react';
import useDistrictListQuery from '../hooks/queries/useDistrictListQuery';
import { formProps } from 'utils/constants';
import SelectComponent from 'components/Select';

type AddressFormType = {
  form: FormInstance;
  type: 'paddyBuyer' | 'farmerGroupLeader' | 'farmerGroup';
  autoReset?: boolean;
};

const addressFormNames = {
  addressName: ['address', 'address'],
  provinceName: ['address', 'stateOrProvince'],
  districtName: ['address', 'district'],
  subDistrictName: ['address', 'subDistrict'],
  villageName: ['address', 'cityOrVillage'],
};

const addressConfig = {
  paddyBuyer: {
    label: 'Address',
    placeholder: 'Complete Address of Paddy Buyer’s house',
  },
  farmerGroupLeader: {
    label: 'Address',
    placeholder: 'Complete Address of Farmer Group Leader',
  },
  farmerGroup: {
    label: 'Farmer Group Leader Address',
    placeholder: 'Complete Address of Farmer Group Leader',
  },
};

const resetFormNames = (isPaddyBuyer: boolean) =>
  isPaddyBuyer
    ? [['agronomist', 'email']]
    : ['seniorAgronomistEmail', 'juniorAgronomistEmail'];

const AddressForm = ({ form, type, autoReset = true }: AddressFormType) => {
  const isPaddyBuyer = type === 'paddyBuyer';
  const isFarmerGroup = type === 'farmerGroup';
  const {
    addressName,
    provinceName,
    districtName,
    subDistrictName,
    villageName,
  } = { ...addressFormNames };
  const rawCountry = Form.useWatch(
    isFarmerGroup ? 'country' : ['address', 'country'],
    form,
  );
  const country = rawCountry === 'Test' ? 'Indonesia' : rawCountry;
  const province = Form.useWatch(provinceName, form);
  const district = Form.useWatch(districtName, form);
  const subDistrict = Form.useWatch(subDistrictName, form);

  const { data, isLoading: isDistrictLoading } = useDistrictListQuery(country);

  const areaData = useMemo(
    () => data?.data?.value && JSON.parse(data?.data.value),
    [data?.data],
  );

  const regionOptions = useMemo(() => {
    const regions = areaData?.data?.region?.split(',');
    return regions
      ? regions.map((region: string) => ({ label: region, value: region }))
      : [];
  }, [areaData]);

  const districtOptions = useMemo(() => {
    const districtData = areaData?.data?.district;
    const districtOfProvince =
      districtData && districtData?.[province]
        ? districtData?.[province]?.split(',')
        : [];
    const data = districtOfProvince.map((district: string) => ({
      label: district,
      value: district,
    }));
    return data;
  }, [areaData, province]);

  const subDistrictOptions = useMemo(() => {
    const subDistrictData = areaData?.data?.subDistrict;
    const subOfDistrict =
      subDistrictData && subDistrictData?.[district]
        ? subDistrictData?.[district]?.split(',')
        : [];
    const data = subOfDistrict.map((taluk: string) => ({
      label: taluk,
      value: taluk,
    }));
    return data;
  }, [areaData, district]);

  const villageOptions = useMemo(() => {
    const villageData = areaData?.data?.village;
    const villageOfTaluk =
      villageData && villageData?.[subDistrict]
        ? villageData?.[subDistrict]?.split(',')
        : [];
    const data = villageOfTaluk.map((village: string) => ({
      label: village,
      value: village,
    }));
    return data;
  }, [areaData, subDistrict]);

  useEffect(() => {
    if (!autoReset) return;
    form.resetFields([
      provinceName,
      districtName,
      subDistrictName,
      villageName,
      ...(resetFormNames(isPaddyBuyer) ?? []),
    ]);
  }, [form, country]);

  useEffect(() => {
    if (!autoReset) return;
    form.resetFields([districtName, subDistrictName, villageName]);
  }, [form, province]);

  useEffect(() => {
    if (!autoReset) return;
    form.resetFields([subDistrictName, villageName]);
  }, [form, district]);

  useEffect(() => {
    if (!autoReset) return;
    form.resetFields([villageName]);
  }, [form, subDistrict]);

  return (
    <Card>
      <Typography.Title level={5} style={{ marginBottom: '24px' }}>
        Address
      </Typography.Title>
      <Form.Item
        name={addressName}
        label={addressConfig[type].label}
        required
        rules={[{ required: true }]}
        {...formProps}
      >
        <Input.TextArea
          placeholder={addressConfig[type].placeholder}
          disabled={isFarmerGroup}
        />
      </Form.Item>
      <Form.Item
        name={provinceName}
        label="Province"
        required
        rules={[{ required: true }]}
        {...formProps}
      >
        <SelectComponent
          placeholder="Select Province"
          options={regionOptions}
          loading={isDistrictLoading}
          disabled={isFarmerGroup}
        />
      </Form.Item>
      <Form.Item
        name={districtName}
        label="District"
        required
        rules={[{ required: true }]}
        {...formProps}
      >
        <SelectComponent
          placeholder="Select District"
          options={districtOptions}
          loading={isDistrictLoading}
          disabled={isFarmerGroup}
        />
      </Form.Item>
      <Form.Item
        name={subDistrictName}
        label="Sub-District"
        required
        rules={[{ required: true }]}
        {...formProps}
      >
        <SelectComponent
          placeholder="Select Sub-District"
          options={subDistrictOptions}
          loading={isDistrictLoading}
          disabled={isFarmerGroup}
        />
      </Form.Item>
      {country === 'Indonesia' && (
        <Form.Item
          name={villageName}
          label="Village"
          required
          rules={[{ required: true }]}
          {...formProps}
        >
          <SelectComponent
            placeholder="Select Village"
            options={villageOptions}
            loading={isDistrictLoading}
            disabled={isFarmerGroup}
          />
        </Form.Item>
      )}
    </Card>
  );
};

export default AddressForm;
